import React, { useState } from "react";
import { useSelector } from "react-redux";
import { FiSearch } from "react-icons/fi";
import { useGetBomOrderHistoryQuery } from "../../redux/api/bomApi";

const BOM_HISTORY_PAGE_SIZE = 8;

/**
 * 将日期格式化为当前浏览器区域的简短时间。
 * @param {string|Date} value 日期值
 * @returns {string} 格式化时间
 */
const formatHistoryDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

/**
 * 渲染可分页、可搜索并可切换任务的 My Parts 历史列表。
 * @param {Object} props 组件属性
 * @param {string} props.sessionId 当前浏览器 BOM 会话 ID
 * @param {string} [props.currentBomId] 当前 BOM ID
 * @param {(bomId: string) => Promise<void>|void} props.onOpen 打开 BOM 回调
 * @param {() => void} [props.onClose] 关闭抽屉回调
 * @returns {JSX.Element} BOM 历史列表
 */
const BomHistoryPanel = ({ sessionId, currentBomId = "", onOpen, onClose }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [page, setPage] = useState(1);
  const [keywordDraft, setKeywordDraft] = useState("");
  const [keyword, setKeyword] = useState("");
  const { data, isLoading, isFetching, error, refetch } = useGetBomOrderHistoryQuery({
    sessionId,
    page,
    limit: BOM_HISTORY_PAGE_SIZE,
    keyword,
  });
  const bomOrders = data?.bomOrders || [];
  const pagination = data?.pagination || { page: 1, pages: 1, total: 0 };

  /**
   * 提交文件名搜索并回到第一页。
   * @param {Event} event 表单事件
   */
  const handleSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setKeyword(keywordDraft.trim());
  };

  return (
    <section className="bom-history-panel" aria-label="My Parts history">
      <div className="bom-history-toolbar">
        <div className="bom-history-heading">
          <div>
            <h2>My Parts ({pagination.total || 0})</h2>
            <p>
              {isAuthenticated
                ? "BOMs saved to your account and this browser session."
                : "BOMs saved in this browser session. Sign in to keep future BOMs with your account."}
            </p>
          </div>
          {onClose && (
            <button type="button" className="bom-history-close" onClick={onClose} aria-label="Close BOM history">
              ×
            </button>
          )}
        </div>

        <form className="bom-history-search" onSubmit={handleSearch}>
          <label>
            <FiSearch aria-hidden="true" />
            <input
              type="search"
              maxLength="100"
              value={keywordDraft}
              onChange={(event) => setKeywordDraft(event.target.value)}
              placeholder="Search by BOM name"
            />
          </label>
          <button type="submit" disabled={isFetching}>Search</button>
        </form>
      </div>

      {isLoading ? (
        <div className="bom-history-state">Loading BOM history...</div>
      ) : error ? (
        <div className="bom-history-state is-error">
          <span>{error?.data?.message || "Failed to load BOM history"}</span>
          <button type="button" onClick={refetch}>Retry</button>
        </div>
      ) : bomOrders.length === 0 ? (
        <div className="bom-history-state">
          {keyword ? "No BOMs match this search." : "No saved BOMs yet. Create or upload one to get started."}
        </div>
      ) : (
        <div className="bom-history-table-wrap">
          <div className="bom-history-table-header" aria-hidden="true">
            <span>Name</span><span>Updated Date</span><span>Rows</span><span>Matched</span><span>Unmatched</span><span>Total</span><span>Action</span>
          </div>
          <div className="bom-history-list">
            {bomOrders.map((bom) => {
              const isCurrent = String(bom._id) === String(currentBomId);
              return (
                <article key={bom._id} className={isCurrent ? "is-current" : ""}>
                  <div className="bom-history-copy">
                    <strong title={bom.originalFileName}>{bom.originalFileName || "Untitled BOM"}</strong>
                    <small title={bom._id}>ID: {bom._id}</small>
                  </div>
                  <time dateTime={bom.updatedAt}>{formatHistoryDate(bom.updatedAt)}</time>
                  <div className="bom-history-summary">
                    <span>{Number(bom.summary?.totalRows || 0)}</span>
                    <span className="is-matched">{Number(bom.summary?.matchedRows || 0)}</span>
                    <span className="is-unmatched">{Number(bom.summary?.unmatchedRows || 0)}</span>
                    <span>${Number(bom.summary?.totalAmount || 0).toFixed(2)}</span>
                  </div>
                  <button type="button" onClick={() => onOpen(String(bom._id))} disabled={isCurrent}>
                    {isCurrent ? "Current" : "Open"}
                  </button>
                </article>
              );
            })}
          </div>
        </div>
      )}

      <div className="bom-history-pagination">
        <span>{pagination.total || 0} BOM(s)</span>
        <div>
          <button type="button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page <= 1 || isFetching}>
            Previous
          </button>
          <span>{pagination.page || page} / {pagination.pages || 1}</span>
          <button type="button" onClick={() => setPage((value) => value + 1)} disabled={page >= pagination.pages || isFetching}>
            Next
          </button>
        </div>
      </div>
    </section>
  );
};

export default BomHistoryPanel;
